'use client';

import { get as _get } from 'lodash-es';
import { useParams } from 'next/navigation';
import { useQuery, useApolloClient } from '@apollo/client/react';
import { makeStyles } from 'tss-react/mui';
import Loading from '@/components/loading';
import ErrorMessage from '@/components/error-message';
import NotFound from '@/components/not-found';
import EventCalendar from '@/components/dynamic-event-calendar';
import { groupCalendarGridQuery, groupCalendarEventsQuery } from '@/apollo/groups';
import CalendarHeader from '@/components/groups/calendar-header';
import GroupCalendarFooter from '@/components/groups/calendar-footer';

const useStyles = makeStyles()((theme) => ({
  addButton: {
    margin: theme.spacing(2, 0),
    whiteSpace: 'nowrap',
  },
  calendar: {
    margin: theme.spacing(2, 0),
  },
  buttons: {
    '& > *:not(:last-child)': {
      marginRight: theme.spacing(1),
    },
  },
}));

type Props = {
  initialGroup?: Record<string, unknown>;
};

const GroupCalendarGridPageClient = ({ initialGroup }: Props) => {
  const { classes } = useStyles();
  const { slug } = useParams();
  const client = useApolloClient();

  const { data, loading, error } = useQuery(groupCalendarGridQuery, {
    variables: { slug },
    ssr: false,
  });
  const group = _get(data, 'group') || initialGroup;

  function handleLoadEvents({ start, end }) {
    return client.query({
      query: groupCalendarEventsQuery,
      variables: {
        slug: group.slug,
        start,
        end,
      },
    }).then(({ data }) => data.group.calendarEvents);
  }

  if (!group && loading) { return <Loading />; }
  if (error && !group) {
    return <ErrorMessage message='An error occurred while loading group.' error={error} />;
  }
  if (!group || group.found) { return <NotFound />; }

  return (
    <>
      <CalendarHeader title="Calendar" tab="calendar" group={group} />

      <div className={classes.calendar}>
        <EventCalendar timeZone={group.city.timezone} loadEvents={handleLoadEvents} />
      </div>

      <GroupCalendarFooter group={group} />
    </>
  );
};

export default GroupCalendarGridPageClient;
